import React, { useState, useEffect } from "react";
import {
  updateSubCategoryByID,
  getAllByCategory,
} from "../../Constant/Api/Api";
import { Modal, TextInput, Button, Select, TagsInput } from "@mantine/core";
import uploadFile from "../../firebase/firebaseUploadFunction";
import { useDisclosure } from "@mantine/hooks";
import { MdDelete } from "react-icons/md";

// Utility function to create slug
const generateSlug = (name) => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-") // Replace non-alphanumeric characters with hyphens
    .replace(/(^-|-$)+/g, ""); // Remove leading and trailing hyphens
};


// Helper function to normalize image data structure
const normalizeImage = (image) => {
  if (!image) return null;

  // If it's already in new format
  if (typeof image === 'object' && image.url) {
    return image;
  }
  // If it's old format (string), convert to new format
  if (typeof image === 'string') {
    return {
      url: image,
      metaData: {
        sizeOfImage: 0, // Default value for existing images
      },
    };
  }
  return null;
};

function EditSubCategory({ subCategory, onClose }) {
  const [opened, { open, close }] = useDisclosure(true);
  const [subCategoryName, setSubCategoryName] = useState(
    subCategory.name || ""
  );
  const [subCategoryDescription, setSubCategoryDescription] = useState(
    subCategory.description || ""
  );
  const [subCategoryImage, setSubCategoryImage] = useState(
    normalizeImage(subCategory.subCategoryImage)
  );
  const [selectedCategory, setSelectedCategory] = useState(
    subCategory.category?._id || ""
  );
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState(subCategory.tags || []);
  const [slug, setSlug] = useState(subCategory.slug || "");
  const [metaName, setMetaName] = useState(subCategory.metaName || "");
  const [metaDescription, setMetaDescription] = useState(
    subCategory.metaDescription || ""
  );
  const [imageUploading, setImageUploading] = useState(false);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    if (subCategory) {
      setSubCategoryName(subCategory.name || "");
      setSubCategoryDescription(subCategory.description || "");
      setSubCategoryImage(normalizeImage(subCategory.subCategoryImage));
      setSelectedCategory(subCategory.category?._id || "");
      setTags(subCategory.tags || []);
      setSlug(subCategory.slug || "");
      setMetaName(subCategory.metaName || "");
      setMetaDescription(subCategory.metaDescription || "");
    }
  }, [subCategory]);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(getAllByCategory, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      if (response.ok) {
        const data = await response.json();
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  const handleSubCategoryChange = (event) => {
    setSubCategoryName(event.target.value);
  };

  const handleDescriptionChange = (event) => {
    setSubCategoryDescription(event.target.value);
  };

  const handleMetaNameChange = (event) => {
    setMetaName(event.target.value);
  };

  const handleMetaDescriptionChange = (event) => {
    setMetaDescription(event.target.value);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const maxSize = 100 * 1024;
    if (file.size > maxSize) {
      alert("Image size should not exceed 100KB");
      e.target.value = '';
      return;
    }

    setImageUploading(true);

    uploadFile(e)
      .then((fireBaseUrl) => {
        // Create new image object structure
        const imageObject = {
          url: fireBaseUrl,
          metaData: {
            sizeOfImage: (file.size / 1000).toFixed(2),
          }
        };
        setSubCategoryImage(imageObject);
        setImageUploading(false);
      })
      .catch((error) => {
        console.error("Error uploading file:", error);
        setImageUploading(false);
      });
  };

  const handleDeleteImage = () => {
    setSubCategoryImage(null);
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
  };

  const handleSlugChange = (event) => {
    setSlug(event.target.value);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const token = localStorage.getItem("token");

    try {
      const formData = {
        name: subCategoryName,
        metaName: metaName || subCategoryName,
        description: subCategoryDescription,
        metaDescription: metaDescription || subCategoryDescription,
        subCategoryImage: subCategoryImage,
        category: selectedCategory,
        tags: tags,
        slug: slug || generateSlug(subCategoryName), // Fallback to generated slug if slug is empty
      };

      const response = await fetch(
        `${updateSubCategoryByID}${subCategory._id}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(formData),
        }
      );

      const data = await response.json();
      if (data) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating subcategory:", error);
    }
  };

  return (
    <div>
      <Modal opened={opened} onClose={onClose} title="Edit Subcategory">
        <form onSubmit={handleSubmit}>
          {/* Image Display Section */}
          {subCategoryImage && (
            <div className="mb-4 relative">
              <img
                src={subCategoryImage.url}
                alt="Subcategory"
                className="w-full h-36 object-cover rounded"
              />
              <div className="absolute top-2 right-2 bg-black bg-opacity-70 text-white px-2 py-1 rounded text-xs">
                {subCategoryImage.metaData?.sizeOfImage} KB
              </div>
              <button
                type="button"
                onClick={handleDeleteImage}
                className="absolute top-2 left-2 bg-red-500 text-white rounded-full p-1"
              >
                <MdDelete size={16} />
              </button>
            </div>
          )}

          <TextInput
            label="Subcategory Name"
            placeholder="Enter subcategory name"
            value={subCategoryName}
            onChange={handleSubCategoryChange}
            required
          />
          <TextInput
            label="Meta Title (SEO)"
            placeholder="Enter meta title"
            value={metaName}
            onChange={handleMetaNameChange}
          />
          <TextInput
            label="Description"
            placeholder="Enter subcategory description"
            value={subCategoryDescription}
            onChange={handleDescriptionChange}
          />
          <TextInput
            label="Meta Description (SEO)"
            placeholder="Enter meta description"
            value={metaDescription}
            onChange={handleMetaDescriptionChange}
          />
          <TextInput
            label="Slug"
            placeholder="Slug"
            value={slug}
            onChange={handleSlugChange}
          />
          <Select
            label="Category"
            placeholder="Select category"
            data={categories.map((category) => ({
              value: category._id,
              label: category.name,
            }))}
            value={selectedCategory}
            onChange={handleCategoryChange}
          />

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SubCategory Image
            </label>
            <input
              type="file"
              accept="image/*"
              placeholder="Select image"
              onChange={handleImageChange}
              disabled={imageUploading}
              className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
            />
            {imageUploading && (
              <p className="text-sm text-blue-600 mt-2">Uploading image...</p>
            )}
          </div>

          <TagsInput
            label="Press Enter to submit a tag"
            placeholder="Enter tag"
            value={tags}
            onChange={setTags}
          />
          <Button type="submit" disabled={imageUploading}>
            {imageUploading ? 'Uploading...' : 'Update'}
          </Button>
        </form>
      </Modal>
    </div>
  );
}

export default EditSubCategory;